import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { Badge } from '@plato/ui-legacy/components/ui/badge';
import { Button } from '@plato/ui-legacy/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui-legacy/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { cn } from '@plato/ui/util/classes';
import { ChevronsRight } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Drawer } from 'vaul';
import { IntercomLauncher } from '../../intercom/components/intercom-launcher.tsx';
import { usePlatoLayoutNavigation } from '../hooks/use-plato-layout-navigation.tsx';
import { CreateNewMenu } from './create-new-menu.tsx';
import { FullWidthNav } from './full-width-nav.tsx';
import { useGlobalSearch } from './global-search-bar-provider.tsx';
import { GlobalSearchBar } from './global-search-bar.tsx';
import { NavLogo } from './nav-logo.tsx';
import {
    createGetItemClassname,
    createGetSubItemClassname,
    createIsSelected,
    createIsSelectedOnlyTopLevel,
    ITEM_ICON_CLASSNAME,
} from './navigation-helpers.ts';
import { SettingsMenu } from './settings-menu.tsx';

export type IconOnlyNavProps = {
    isDesktopScreenSize: boolean;
    smallScreenDrawOpen: boolean;
    setSmallScreenDrawOpen: (open: boolean) => void;
    setDesktopSidebarOpen: (open: boolean) => void;
    closeDrawer: () => void;
    /** Whether to render the rep-oriented "Create new" action. */
    showCreateNew: boolean;
};

export function IconOnlyNav({
    isDesktopScreenSize,
    smallScreenDrawOpen,
    setSmallScreenDrawOpen,
    setDesktopSidebarOpen,
    closeDrawer,
    showCreateNew,
}: IconOnlyNavProps) {
    const { t } = useTranslation('common');
    const location = useLocation();
    const navigate = useNavigate();
    const { navigation, activeSection } = usePlatoLayoutNavigation();

    const { open: globalSearchOpen } = useGlobalSearch();
    const isSelected = createIsSelected(activeSection, location.pathname);
    const isSelectedOnlyTopLevel = createIsSelectedOnlyTopLevel(activeSection, isSelected);
    const getItemClassname = createGetItemClassname(location.pathname, isSelected);
    const getSubItemClassname = createGetSubItemClassname(location.pathname, isSelected);

    return (
        <Drawer.Root
            shouldScaleBackground
            direction="left"
            open={smallScreenDrawOpen}
            onOpenChange={setSmallScreenDrawOpen}
        >
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 z-30 bg-black/20 " />
                <Drawer.Content
                    onCloseAutoFocus={(event) => {
                        // Global search opens as this drawer closes, and has already focused
                        // its field inside the tap so the software keyboard comes up. vaul
                        // restores focus to the drawer's trigger here, which would blur that
                        // field and drop the keyboard straight back down.
                        if (globalSearchOpen) {
                            event.preventDefault();
                        }
                    }}
                    className={cn(
                        'fixed bottom-0 left-0 right-0 z-30 mt-24 flex h-full w-64',
                        'flex-col bg-white shadow-lg',
                    )}
                >
                    <FullWidthNav
                        isDesktopScreenSize={false}
                        closeDrawer={closeDrawer}
                        setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                        setDesktopSidebarOpen={setDesktopSidebarOpen}
                        showCreateNew={showCreateNew}
                    />
                </Drawer.Content>
            </Drawer.Portal>
            <div className="flex h-full flex-col gap-3 py-3 relative">
                <Link to="/" className="flex shrink-0 items-center justify-center h-8">
                    <NavLogo className="h-7" />
                </Link>
                <div className="flex flex-col items-center">
                    {isDesktopScreenSize ? (
                        <Button
                            size="icon-s"
                            variant="outline"
                            aria-label={t('nav__expand')}
                            onClick={() => {
                                setDesktopSidebarOpen(true);
                            }}
                        >
                            <ChevronsRight className="flex h-4 w-4 shrink-0 " />
                        </Button>
                    ) : (
                        <Drawer.Trigger asChild>
                            <Button size="icon-s" variant="outline" aria-label={t('nav__expand')}>
                                <ChevronsRight className="flex h-4 w-4 shrink-0 " />
                            </Button>
                        </Drawer.Trigger>
                    )}
                </div>
                <GlobalSearchBar showFullInput={false} className="mx-auto" />
                {showCreateNew && (
                    <div className="mx-auto">
                        <CreateNewMenu onClose={closeDrawer} showFullInput={false} />
                    </div>
                )}
                <nav>
                    <ul role="navigation" className="flex flex-col items-center gap-3">
                        {navigation.map((group, groupIndex) => (
                            <Fragment key={group.id}>
                                {groupIndex > 0 && (
                                    <li role="separator" className="py-1">
                                        <div className="mx-auto h-px w-6 bg-neutral-200" />
                                    </li>
                                )}
                                {group.items.map((item) => (
                                    <li key={item.id}>
                                        <Tooltip>
                                            {item.subItems && (
                                                <DropdownMenu>
                                                    {/* The tooltip attaches to a wrapper: rendering it as
                                                        the DropdownMenuTrigger would shadow the menu's
                                                        own click handler. */}
                                                    <TooltipTrigger
                                                        delay={0}
                                                        render={<span className="inline-flex" />}
                                                    >
                                                        <DropdownMenuTrigger asChild>
                                                            <div
                                                                className={cn(
                                                                    isSelectedOnlyTopLevel(item)
                                                                        ? 'bg-blue-5 text-blue-600'
                                                                        : 'text-neutral-700',
                                                                    !!item.href && 'hover:bg-white',
                                                                    'group flex gap-x-3 rounded-md p-2',
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={ITEM_ICON_CLASSNAME}
                                                                    aria-hidden="true"
                                                                />
                                                                <span className="sr-only">{item.name}</span>
                                                            </div>
                                                        </DropdownMenuTrigger>
                                                    </TooltipTrigger>
                                                    <DropdownMenuContent
                                                        align="start"
                                                        side="right"
                                                        className="z-30"
                                                    >
                                                        {item.subItems.map((subItem) => (
                                                            <DropdownMenuItem
                                                                key={subItem.id}
                                                                className={getSubItemClassname(subItem)}
                                                                onSelect={() => navigate(subItem.href)}
                                                            >
                                                                <subItem.icon
                                                                    className={ITEM_ICON_CLASSNAME}
                                                                    aria-hidden="true"
                                                                />
                                                                <span>{subItem.name}</span>
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                            {!item.subItems && !!item.href && (
                                                <TooltipTrigger
                                                    delay={0}
                                                    render={
                                                        <Link
                                                            to={item.href}
                                                            className={getItemClassname(item)}
                                                        />
                                                    }
                                                >
                                                    <div className="relative">
                                                        <item.icon
                                                            className={ITEM_ICON_CLASSNAME}
                                                            aria-hidden="true"
                                                        />
                                                        {!!item.count && (
                                                            <Badge
                                                                variant="destructive"
                                                                className={cn(
                                                                    'absolute right-0 top-0 h-4 min-w-4 -translate-y-1/2 translate-x-1/2',
                                                                    'justify-center rounded-full px-1 py-0.5 text-[10px] font-normal',
                                                                )}
                                                            >
                                                                <NumberOrEmpty>{item.count}</NumberOrEmpty>
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    <span className="sr-only">{item.name}</span>
                                                </TooltipTrigger>
                                            )}
                                            <TooltipContent side="right">{item.name}</TooltipContent>
                                        </Tooltip>
                                    </li>
                                ))}
                            </Fragment>
                        ))}
                    </ul>
                </nav>
                <div className="grow"></div>
                <ul className="flex flex-col items-center gap-3">
                    <li>
                        <IntercomLauncher expanded={false} />
                    </li>
                    <li>
                        <SettingsMenu expanded={false} />
                    </li>
                </ul>
            </div>
        </Drawer.Root>
    );
}
